import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { FLAG_FOR } from './Flags';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '简体中文' },
  { code: 'hi', label: 'हिन्दी' },
];

const LanguageSwitcher = ({ compact = false, variant = 'desktop' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];
  const Flag = FLAG_FOR[current.code];

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid={`language-switcher-${variant}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.label}`}
        data-testid="language-switcher-trigger"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:border-[#5FE0C8]/50 text-[12.5px] text-[#C9C5B8] hover:text-[#ECE9DF] transition-colors"
      >
        <Flag size={18} className="rounded-[2px] overflow-hidden shrink-0" />
        {!compact && <span className="hidden lg:inline">{current.label}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[180px] rounded-xl border border-white/10 bg-[#0B1C18] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] overflow-hidden z-50"
          data-testid="language-switcher-menu"
        >
          {LANGS.map((l) => {
            const F = FLAG_FOR[l.code];
            const isActive = l.code === current.code;
            return (
              <li key={l.code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => change(l.code)}
                  data-testid={`language-option-${l.code}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-left transition-colors ${
                    isActive ? 'bg-[#0E2A24] text-[#5FE0C8]' : 'text-[#C9C5B8] hover:bg-[#0E2A24] hover:text-[#ECE9DF]'
                  }`}
                >
                  <F size={20} className="rounded-[2px] overflow-hidden shrink-0" />
                  <span className="flex-1">{l.label}</span>
                  {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#5FE0C8]" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
